import React, { useState } from "react";
import Context from "../../../../Context";
import { useToast } from "../../../../components/ui";
import { resizeImage } from "../../../../util";
import {
  User,
  ArrowRight,
  Upload,
  X,
  AlertCircle,
  CheckCircle2,
  Search,
  FileText,
  ShieldCheck,
  Phone,
  RefreshCw,
} from "lucide-react";

const TRANSFER_REASONS = [
  "ลูกค้ามีหลายบัญชี / สั่งซื้อผิดเบอร์",
  "ลูกค้าสลับการใช้งานระหว่าง LINE กับ Web",
  "ลูกค้าพิมพ์เบอร์โทรผิดขณะสั่งซื้อ",
  "ระงับการใช้งานบัญชีเดิม / ย้ายมาบัญชีใหม่",
  "อื่นๆ (ระบุในรายละเอียด)",
];

export const OrderTransferModal = ({
  isOpen,
  onClose,
  order,
  currentUser,
  onSuccess,
}) => {
  const { callApi } = Context();
  const { toast } = useToast();

  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [targetUser, setTargetUser] = useState(null);
  const [searchError, setSearchError] = useState("");

  const [reason, setReason] = useState(TRANSFER_REASONS[0]);
  const [detail, setDetail] = useState("");
  const [syncRecipient, setSyncRecipient] = useState(true);
  const [evidenceImages, setEvidenceImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen || !order) return null;

  // ค้นหาผู้ใช้ปลายทางด้วยเบอร์โทรหรือ User ID
  const handleSearchUser = async (e) => {
    if (e) e.preventDefault();
    const query = searchQuery.trim();
    if (!query) {
      setSearchError("กรุณากรอกเบอร์โทรหรือ User ID");
      return;
    }

    setSearching(true);
    setSearchError("");
    setTargetUser(null);

    try {
      // 1. ลองค้นหาด้วย User ID ตรงๆ
      const idResult = await callApi(
        "users-getUserInfoAdmin",
        { userId: query },
        { notShowLoading: true }
      );

      if (idResult?.status && idResult.data) {
        if (idResult.data.userId === order.userId) {
          setSearchError("บัญชีนี้เป็นเจ้าของคำสั่งซื้อปัจจุบันอยู่แล้ว");
          return;
        }
        setTargetUser(idResult.data);
        return;
      }

      // 2. ถ้าไม่เจอด้วย ID ให้ลองค้นด้วยเบอร์โทร
      const cleanPhone = query.replace(/\D/g, "");
      const phoneResult = await callApi(
        "users-getAll",
        { queries: [["phone", "==", cleanPhone]] },
        { notShowLoading: true }
      );

      const foundUsers = phoneResult?.users || phoneResult?.data || [];
      if (Array.isArray(foundUsers) && foundUsers.length > 0) {
        const found = foundUsers[0];
        if (found.id === order.userId || found.userId === order.userId) {
          setSearchError("บัญชีนี้เป็นเจ้าของคำสั่งซื้อปัจจุบันอยู่แล้ว");
          return;
        }
        setTargetUser(found);
        return;
      }

      setSearchError("ไม่พบบัญชีผู้ใช้งานที่ตรงกับข้อมูลที่ค้นหา");
    } catch (err) {
      console.error("Search user error:", err);
      setSearchError("เกิดข้อผิดพลาดในการค้นหาผู้ใช้งาน");
    } finally {
      setSearching(false);
    }
  };

  // อัปโหลดรูปหลักฐาน
  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (evidenceImages.length >= 5) {
      toast({
        className: "bg-rose-600 text-white font-semibold py-2 px-3 rounded-md",
        title: "แนบรูปหลักฐานได้สูงสุด 5 รูป",
      });
      return;
    }

    try {
      const base64 = await resizeImage(file, 800, 1600);
      if (base64) {
        setEvidenceImages((prev) => [
          ...prev,
          {
            name: file.name,
            base64,
          },
        ]);
      } else {
        toast({
          className: "bg-rose-600 text-white font-semibold py-2 px-3 rounded-md",
          title: "ไฟล์รูปภาพไม่ถูกต้อง (รองรับ JPG, PNG)",
        });
      }
    } catch (err) {
      console.error("Resize image error:", err);
    } finally {
      e.target.value = "";
    }
  };

  const removeEvidence = (index) => {
    setEvidenceImages((prev) => prev.filter((_, idx) => idx !== index));
  };

  // ยื่นคำขอ
  const handleSubmit = async () => {
    if (!targetUser) {
      toast({
        className: "bg-rose-600 text-white font-semibold py-2 px-3 rounded-md",
        title: "กรุณาค้นหาและเลือกบัญชีผู้ใช้ปลายทาง",
      });
      return;
    }

    const targetUid = targetUser.id || targetUser.userId;

    if (!reason) {
      toast({
        className: "bg-rose-600 text-white font-semibold py-2 px-3 rounded-md",
        title: "กรุณาระบุเหตุผลการขอย้าย",
      });
      return;
    }

    setSubmitting(true);
    try {
      const result = await callApi("orders-requestTransfer", {
        orderId: order.id,
        targetUserId: targetUid,
        reason,
        detail,
        syncRecipientInfo: syncRecipient,
        evidenceImages: evidenceImages.map((img) => img.base64),
      });

      if (result?.status) {
        toast({
          className: "bg-emerald-600 text-white font-semibold py-2 px-3 rounded-md",
          title: (
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              <span>ส่งคำขอย้ายคำสั่งซื้อเรียบร้อยแล้ว รอการอนุมัติ</span>
            </div>
          ),
        });
        if (onSuccess) onSuccess();
        onClose();
      } else {
        toast({
          className: "bg-rose-600 text-white font-semibold py-2 px-3 rounded-md",
          title: result?.message || "ไม่สามารถยื่นคำขอย้ายได้",
        });
      }
    } catch (err) {
      console.error("Request transfer error:", err);
      toast({
        className: "bg-rose-600 text-white font-semibold py-2 px-3 rounded-md",
        title: "เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const currentUserName = currentUser?.firstName
    ? `${currentUser.firstName} ${currentUser.lastName || ""}`
    : order.firstName
    ? `${order.firstName} ${order.lastName || ""}`
    : "ไม่ระบุชื่อ";

  const targetUserName = targetUser?.firstName
    ? `${targetUser.firstName} ${targetUser.lastName || ""}`
    : "ไม่ระบุชื่อ";

  const targetUserUid = targetUser?.id || targetUser?.userId || "";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 overflow-y-auto">
      <div className="bg-white w-full max-w-2xl rounded-md shadow-2xl border border-slate-200 overflow-hidden my-8 animate-in fade-in-50 zoom-in-95 duration-150">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50/80">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-blue/10 text-blue rounded-md">
              <RefreshCw className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-800">
                ขอย้ายเจ้าของคำสั่งซื้อ
              </h3>
              <p className="text-xs text-slate-500 font-medium">
                คำสั่งซื้อ: <span className="font-mono font-bold text-slate-700">{order.id}</span>
                {order.roundDate ? ` (งวด ${order.roundDate})` : ""}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 rounded-md transition cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto">
          {/* Step 1: ค้นหาบัญชีผู้ใช้ปลายทาง */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
              <Search className="w-4 h-4 text-blue" />
              <span>1. ค้นหาบัญชีลูกค้าปลายทางที่จะย้ายไป</span>
            </label>
            <form onSubmit={handleSearchUser} className="flex gap-2">
              <input
                type="text"
                placeholder="กรอกเบอร์โทรศัพท์ (เช่น 0812345678) หรือ User ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                disabled={searching}
                className="flex-1 h-10 px-3 text-sm font-medium text-slate-800 bg-white border border-slate-300 rounded-md focus:outline-none focus:border-blue transition-colors disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={searching}
                className="h-10 px-4 bg-navy hover:bg-navy/90 text-white font-bold text-sm rounded-md shadow-xs flex items-center gap-2 cursor-pointer transition disabled:opacity-50 shrink-0"
              >
                {searching ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>กำลังค้นหา...</span>
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4" />
                    <span>ค้นหา</span>
                  </>
                )}
              </button>
            </form>
            {searchError && (
              <p className="text-xs font-semibold text-rose-600 flex items-center gap-1 mt-1">
                <AlertCircle className="w-3.5 h-3.5" /> {searchError}
              </p>
            )}
          </div>

          {/* Comparison Cards: เจ้าของเดิม ➡️ เจ้าของใหม่ */}
          <div className="bg-slate-50 p-4 rounded-md border border-slate-200 space-y-3">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              เปรียบเทียบการโอนย้ายกรรมสิทธิ์
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-stretch">
              {/* บัญชีเดิม */}
              <div className="bg-white p-3.5 rounded-md border border-slate-200 flex flex-col justify-between">
                <div>
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
                    เจ้าของเดิม (Current)
                  </span>
                  <p className="text-sm font-bold text-slate-800 truncate">
                    {currentUserName}
                  </p>
                  <p className="text-xs text-slate-500 font-mono mt-0.5 flex items-center gap-1">
                    <Phone className="w-3 h-3 text-slate-400" />
                    {order.phone || currentUser?.phone || "ไม่ระบุเบอร์"}
                  </p>
                </div>
                <div className="mt-2 pt-2 border-t border-slate-100">
                  <span className="text-[10px] font-mono text-slate-400 block truncate">
                    UID: {order.userId}
                  </span>
                </div>
              </div>

              {/* บัญชีใหม่ */}
              <div
                className={`p-3.5 rounded-md border flex flex-col justify-between transition-all ${
                  targetUser
                    ? "bg-blue/5 border-blue/40 shadow-xs"
                    : "bg-white border-dashed border-slate-300"
                }`}
              >
                {targetUser ? (
                  <>
                    <div>
                      <span className="text-[11px] font-bold text-blue uppercase tracking-wider block mb-1">
                        เจ้าของใหม่ (Target)
                      </span>
                      <p className="text-sm font-bold text-slate-900 truncate">
                        {targetUserName}
                      </p>
                      <p className="text-xs text-slate-600 font-mono mt-0.5 flex items-center gap-1">
                        <Phone className="w-3 h-3 text-blue" />
                        {targetUser.phone || "ไม่ระบุเบอร์"}
                      </p>
                    </div>
                    <div className="mt-2 pt-2 border-t border-blue/20 flex items-center justify-between">
                      <span className="text-[10px] font-mono text-blue/80 truncate">
                        UID: {targetUserUid}
                      </span>
                      <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded-md">
                        เลือกแล้ว
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-center py-4 text-slate-400">
                    <User className="w-8 h-8 stroke-1 text-slate-300 mb-1" />
                    <span className="text-xs font-semibold">
                      ค้นหาและเลือกบัญชีปลายทาง
                    </span>
                  </div>
                )}
              </div>
            </div>

            {targetUser && (
              <label className="flex items-center gap-2 cursor-pointer pt-1">
                <input
                  type="checkbox"
                  checked={syncRecipient}
                  onChange={(e) => setSyncRecipient(e.target.checked)}
                  className="rounded-md border-slate-300 text-blue focus:ring-blue h-4 w-4"
                />
                <span className="text-xs font-semibold text-slate-700">
                  อัปเดตชื่อ-เบอร์โทรผู้รับในคำสั่งซื้อให้ตรงกับบัญชีใหม่ด้วย
                </span>
              </label>
            )}
          </div>

          {/* Step 2: เหตุผลการขอย้าย */}
          <div className="space-y-3">
            <label className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
              <FileText className="w-4 h-4 text-blue" />
              <span>2. สาเหตุและรายละเอียดการขอย้าย</span>
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full h-10 px-3 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-md focus:outline-none focus:border-blue transition-colors cursor-pointer"
            >
              {TRANSFER_REASONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
            <textarea
              rows={2}
              placeholder="ระบุรายละเอียดเพิ่มเติม (เช่น เลขที่เคส, ข้อความที่ลูกค้าร้องขอ, หรือหมายเหตุสำหรับผู้อนุมัติ)..."
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              className="w-full p-2.5 text-sm font-medium text-slate-800 bg-white border border-slate-300 rounded-md focus:outline-none focus:border-blue transition-colors"
            />
          </div>

          {/* Step 3: แนบรูปหลักฐาน */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                <Upload className="w-4 h-4 text-blue" />
                <span>3. แนบหลักฐาน (แชทลูกค้า / สลิป / บัตร ปชช.)</span>
              </label>
              <span className="text-xs text-slate-400 font-medium">
                ({evidenceImages.length}/5 รูป)
              </span>
            </div>

            <div className="flex flex-wrap gap-2.5 items-center">
              {evidenceImages.map((img, idx) => (
                <div
                  key={idx}
                  className="relative w-20 h-20 rounded-md border border-slate-200 overflow-hidden bg-slate-100 group shadow-2xs"
                >
                  <img
                    src={img.base64}
                    alt={`หลักฐาน ${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removeEvidence(idx)}
                    className="absolute top-1 right-1 p-1 bg-rose-600/90 text-white rounded-md opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer shadow-xs"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}

              {evidenceImages.length < 5 && (
                <label className="w-20 h-20 border-2 border-dashed border-slate-300 hover:border-blue hover:bg-blue/5 rounded-md flex flex-col items-center justify-center gap-1 cursor-pointer transition text-slate-400 hover:text-blue">
                  <Upload className="w-4 h-4" />
                  <span className="text-[10px] font-bold">เพิ่มรูป</span>
                  <input
                    type="file"
                    accept="image/png, image/jpeg, image/jpg"
                    onChange={handleUploadImage}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          </div>

          {/* Notice Alert */}
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-md flex items-start gap-2.5 text-xs text-amber-900 font-medium">
            <ShieldCheck className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-bold">ระบบตรวจสอบความปลอดภัย (Two-Person Approval)</p>
              <p className="mt-0.5 text-amber-800">
                เมื่อยื่นคำขอแล้ว ข้อมูลจะถูกส่งไปยังคิวอนุมัติเพื่อให้ **Dev หรือ Team Lead Admin** ตรวจสอบหลักฐานก่อนการโอนย้ายกรรมสิทธิ์จริงในระบบ
              </p>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-3.5 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2.5">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-sm font-semibold text-slate-600 hover:text-slate-800 px-4 py-2 rounded-md hover:bg-slate-200/60 transition cursor-pointer disabled:opacity-50"
          >
            ยกเลิก
          </button>
          <button
            type="button"
            disabled={!targetUser || submitting}
            onClick={handleSubmit}
            className="py-2.5 px-4 text-sm font-bold rounded-md bg-blue hover:bg-blue/90 text-white shadow-xs transition flex items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>กำลังส่งคำขอ...</span>
              </>
            ) : (
              <>
                <ArrowRight className="w-4 h-4" />
                <span>ส่งคำขอย้ายคำสั่งซื้อ</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderTransferModal;
